import React from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Wrench, AlertCircle, ShieldCheck, AlertTriangle, ChevronRight, ChevronDown, User } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';

type JobCardStatus = 'active' | 'completed' | 'approved' | 'voided';

interface JobCardKanban {
  id: string;
  // Workflow status — was `workflowStatus`; merged into `status`.
  status: JobCardStatus;
  jobSheetNumber?: string;
  workDescription?: string | null;
  asset?: { id: string; fleetNumber?: string } | null;
  assignedTo?: Array<{ id: string; fullName?: string }>;
}

const TODAY = () => new Date(new Date().toDateString()); // strips time
const DAYS = (a: Date, b: Date) => Math.floor((a.getTime() - b.getTime()) / 86400000);

interface JobCard { id: string; status: string; jobSheetNumber?: string; asset?: any }
interface JobCardStats { active: number; completed: number; approved: number; voided: number; total: number }
interface UpcomingService { id: string; nextServiceDate: string | null; asset?: any; status?: string }
interface WofRenewal { id: string; expiryDate: string | null; asset?: any; wofCof?: string }
interface LicenseRenewal { id: string; dateOfIssue: string | null; licencePeriod: number | null; asset?: any }
interface Issue { id: string; status?: string | null; priority?: string | null; resolvedAt?: string | null; asset?: any; description?: string | null }

export function JobCardDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'kanban' ? 'kanban' : 'overview';
  // Bucket counts come from a server-side aggregate so this scales past 1000 records.
  const { data: jcStats } = useQuery<JobCardStats>({
    queryKey: ['dashboard', '/fleet/job-cards/stats'],
    queryFn: async () => (await api<JobCardStats>('/fleet/job-cards/stats')).data!,
  });
  // Only the active subset is hydrated — used to populate the "in workshop" list below.
  const { data: activeJobCards = [] } = useQuery<JobCard[]>({
    queryKey: ['dashboard', '/fleet/job-cards', 'active'],
    queryFn: async () => (await api<JobCard[]>('/fleet/job-cards?status=active&limit=1000')).data || [],
  });
  const { data: upcoming = [] } = useQuery<UpcomingService[]>({
    queryKey: ['dashboard', '/upcoming-services'],
    queryFn: async () => (await api<UpcomingService[]>('/upcoming-services?limit=1000')).data || [],
  });
  const { data: wofs = [] } = useQuery<WofRenewal[]>({
    queryKey: ['dashboard', '/wof-renewals'],
    queryFn: async () => (await api<WofRenewal[]>('/wof-renewals?limit=1000')).data || [],
  });
  const { data: licences = [] } = useQuery<LicenseRenewal[]>({
    queryKey: ['dashboard', '/license-renewals'],
    queryFn: async () => (await api<LicenseRenewal[]>('/license-renewals?limit=1000')).data || [],
  });
  const { data: issues = [] } = useQuery<Issue[]>({
    queryKey: ['dashboard', '/issues'],
    queryFn: async () => (await api<Issue[]>('/issues?limit=1000')).data || [],
  });

  // Job Cards: bucket counts (4-status model) — sourced from /stats aggregate.
  const jcCounts = {
    active:    jcStats?.active    ?? 0,
    completed: jcStats?.completed ?? 0,
    approved:  jcStats?.approved  ?? 0,
    voided:    jcStats?.voided    ?? 0,
  };
  const jcMax = Math.max(jcCounts.active, jcCounts.completed, jcCounts.approved, jcCounts.voided, 1);
  const jcTotal = jcStats?.total ?? 0;

  // Maintenance (upcoming-services): split by overdue vs upcoming.
  const today = TODAY();
  const upcomingOverdue = upcoming.filter((u) => u.nextServiceDate && new Date(u.nextServiceDate) < today).length;
  const upcomingUpcoming = upcoming.filter((u) => u.nextServiceDate && new Date(u.nextServiceDate) >= today).length;
  const mMax = Math.max(upcomingOverdue, upcomingUpcoming, 1);
  const mTotal = upcomingOverdue + upcomingUpcoming;

  // Issues — open vs resolved, plus priority breakdown.
  const isResolved = (i: Issue) =>
    !!i.resolvedAt || (i.status ?? '').toLowerCase() === 'resolved' || (i.status ?? '').toLowerCase() === 'closed';
  const openIssues = issues.filter((i) => !isResolved(i));
  const issueByPriority = {
    high:   openIssues.filter((i) => (i.priority ?? '').toLowerCase() === 'high').length,
    medium: openIssues.filter((i) => (i.priority ?? '').toLowerCase() === 'medium').length,
    low:    openIssues.filter((i) => (i.priority ?? '').toLowerCase() === 'low').length,
    other:  openIssues.filter((i) => !['high', 'medium', 'low'].includes((i.priority ?? '').toLowerCase())).length,
  };
  const issueMax = Math.max(issueByPriority.high, issueByPriority.medium, issueByPriority.low, issueByPriority.other, 1);

  // Compliance — Rego (licence) / WoF / CoF.
  const licenceExpiry = (l: LicenseRenewal) => {
    if (!l.dateOfIssue || !l.licencePeriod) return null;
    const d = new Date(l.dateOfIssue);
    d.setMonth(d.getMonth() + Number(l.licencePeriod || 0));
    return d;
  };
  const regoOverdue = licences.filter((l) => {
    const exp = licenceExpiry(l);
    return exp && exp < today;
  }).length;
  const regoUpcoming = licences.filter((l) => {
    const exp = licenceExpiry(l);
    if (!exp) return false;
    const days = DAYS(exp, today);
    return days >= 0 && days <= 30;
  }).length;

  const wofRows = wofs.filter((w) => (w.wofCof ?? '').toUpperCase() !== 'COF');
  const cofRows = wofs.filter((w) => (w.wofCof ?? '').toUpperCase() === 'COF');
  function expiryCounts(rows: WofRenewal[]) {
    const overdue = rows.filter((r) => r.expiryDate && new Date(r.expiryDate) < today).length;
    const upcoming = rows.filter((r) => {
      if (!r.expiryDate) return false;
      const days = DAYS(new Date(r.expiryDate), today);
      return days >= 0 && days <= 30;
    }).length;
    return { overdue, upcoming };
  }
  const wofCounts = expiryCounts(wofRows);
  const cofCounts = expiryCounts(cofRows);
  const compMax = Math.max(regoOverdue, regoUpcoming, wofCounts.overdue, wofCounts.upcoming, cofCounts.overdue, cofCounts.upcoming, 1);
  const compOverdueTotal = regoOverdue + wofCounts.overdue + cofCounts.overdue;
  const compUpcomingTotal = regoUpcoming + wofCounts.upcoming + cofCounts.upcoming;

  // Latest assets in workshop = job cards still active, distinct by asset, top 5.
  const inWorkshop = activeJobCards;
  const byAsset = new Map<string, { asset: any; jobCards: number }>();
  for (const j of inWorkshop) {
    const a = j.asset;
    if (!a?.id) continue;
    const cur = byAsset.get(a.id) ?? { asset: a, jobCards: 0 };
    cur.jobCards++;
    byAsset.set(a.id, cur);
  }
  const workshopRows = [...byAsset.values()].slice(0, 5);

  // Attention required = top compliance issues by days overdue.
  // An asset can have multiple historic licence / WoF / CoF records; only
  // the LATEST per (asset, category) reflects current compliance. Older
  // expired records are just history and must not show as separate overdue
  // reasons. (Was causing e.g. "fleet 5 — 16 Days overdue for CoF" to
  // appear 3× when 3 historic CoF rows all expired on the same date.)
  // Each reason links to the compliance report that owns that record, pre-filtered
  // to the asset's fleet number so the operator can act on the actual row.
  //   Rego    → /fleet-extras/license-renewals
  //   WoF/CoF → /fleet-extras/wof-renewals
  //   Service → /fleet-extras/upcoming-services
  type AttnCategory = 'rego' | 'wof' | 'cof' | 'service';
  type AttnReason = { text: string; category: AttnCategory };
  type Attn = { assetLabel: string; reasons: AttnReason[] };
  const attnByAsset = new Map<string, Attn>();
  function addReason(asset: any, category: AttnCategory, text: string) {
    const key = asset?.id ?? 'unknown';
    const label = asset?.fleetNumber || asset?.registrationNumber || 'Unknown asset';
    const cur: Attn = attnByAsset.get(key) ?? { assetLabel: label, reasons: [] };
    cur.reasons.push({ text, category });
    attnByAsset.set(key, cur);
  }
  function reasonHref(assetLabel: string, category: AttnCategory): string {
    const q = encodeURIComponent(assetLabel);
    switch (category) {
      case 'rego':    return `/fleet-extras/license-renewals?asset=${q}`;
      case 'wof':
      case 'cof':     return `/fleet-extras/wof-renewals?asset=${q}`;
      case 'service': return `/fleet-extras/upcoming-services?asset=${q}`;
    }
  }
  function pickLatest<T>(rows: T[], assetId: (r: T) => string | undefined, expiry: (r: T) => Date | null, bucket: (r: T) => string = () => '_') {
    const latest = new Map<string, { row: T; exp: Date }>();
    for (const r of rows) {
      const id = assetId(r);
      const exp = expiry(r);
      if (!id || !exp) continue;
      const k = `${id}|${bucket(r)}`;
      const prev = latest.get(k);
      if (!prev || exp > prev.exp) latest.set(k, { row: r, exp });
    }
    return [...latest.values()];
  }
  // Rego — latest licence per asset
  for (const { row: l, exp } of pickLatest(licences, (l) => l.asset?.id, licenceExpiry)) {
    if (exp < today) addReason(l.asset, 'rego', `${DAYS(today, exp)} Days overdue for Rego`);
  }
  // WoF / CoF — latest per asset PER category (WoF vs CoF are separate certs)
  for (const { row: w, exp } of pickLatest(wofs, (w) => w.asset?.id, (w) => w.expiryDate ? new Date(w.expiryDate) : null, (w) => (w.wofCof ?? '').toUpperCase() === 'COF' ? 'CoF' : 'WoF')) {
    if (exp < today) {
      const isCof = (w.wofCof ?? '').toUpperCase() === 'COF';
      const label = isCof ? 'CoF' : 'WoF';
      addReason(w.asset, isCof ? 'cof' : 'wof', `${DAYS(today, exp)} Days overdue for ${label}`);
    }
  }
  // Service — latest upcoming-services row per asset
  for (const { row: u, exp } of pickLatest(upcoming, (u) => u.asset?.id, (u) => u.nextServiceDate ? new Date(u.nextServiceDate) : null)) {
    if (exp < today) addReason(u.asset, 'service', `${DAYS(today, exp)} Days overdue for Service`);
  }
  const attentionItems = [...attnByAsset.values()].slice(0, 8);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Job Card Dashboard</h1>
        <p className="text-sm text-muted-foreground">Manage workshop operations and maintenance.</p>
      </div>

      <Tabs
        value={tab}
        onValueChange={(v) => {
          const next = new URLSearchParams(searchParams);
          if (v === 'overview') next.delete('tab'); else next.set('tab', v);
          setSearchParams(next, { replace: true });
        }}
        className="space-y-4"
      >
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="kanban">Kanban</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-0">
      {/* ---- Top row: 4 KPI cards ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        {/* Job Cards */}
        <KpiCard title="Job Cards" icon={FileText} totalLabel={`Total ${jcTotal}`}>
          <div className="flex items-end justify-around h-32 px-2">
            <Bar value={jcCounts.active}    max={jcMax} label="Active"    colour="bg-blue-500" />
            <Bar value={jcCounts.completed} max={jcMax} label="Completed" colour="bg-amber-500" />
            <Bar value={jcCounts.approved}  max={jcMax} label="Approved"  colour="bg-green-500" />
            <Bar value={jcCounts.voided}    max={jcMax} label="Voided"    colour="bg-red-500" />
          </div>
        </KpiCard>

        {/* Servicing */}
        <KpiCard title="Servicing" icon={Wrench} totalLabel={`Total ${mTotal}`}>
          <div className="flex items-end justify-around h-32 px-2">
            <Bar value={upcomingOverdue}  max={mMax} label="Overdue"  colour="bg-red-500" />
            <Bar value={upcomingUpcoming} max={mMax} label="Upcoming" colour="bg-green-500" />
          </div>
          <Legend overdue={upcomingOverdue} upcoming={upcomingUpcoming} />
        </KpiCard>

        {/* Issues — open issues by priority */}
        <KpiCard title="Issues" icon={AlertCircle} totalLabel={`Total ${openIssues.length}`}>
          {openIssues.length === 0 ? (
            <div className="h-32 flex items-center justify-center text-sm text-muted-foreground">
              No open issues
            </div>
          ) : (
            <div className="flex items-end justify-around h-32 px-2">
              <Bar value={issueByPriority.high}   max={issueMax} label="High"   colour="bg-red-500" />
              <Bar value={issueByPriority.medium} max={issueMax} label="Medium" colour="bg-amber-500" />
              <Bar value={issueByPriority.low}    max={issueMax} label="Low"    colour="bg-blue-500" />
              <Bar value={issueByPriority.other}  max={issueMax} label="Other"  colour="bg-muted-foreground/40" />
            </div>
          )}
        </KpiCard>

        {/* Compliance */}
        <KpiCard title="Compliance" icon={ShieldCheck} totalLabel={`Total ${compOverdueTotal + compUpcomingTotal}`}>
          <div className="space-y-2 px-2 py-3 text-xs">
            <ComplianceRow label="Rego" overdue={regoOverdue}   upcoming={regoUpcoming}   max={compMax} />
            <ComplianceRow label="WOF"  overdue={wofCounts.overdue} upcoming={wofCounts.upcoming} max={compMax} />
            <ComplianceRow label="COF"  overdue={cofCounts.overdue} upcoming={cofCounts.upcoming} max={compMax} />
          </div>
          <Legend overdue={compOverdueTotal} upcoming={compUpcomingTotal} />
        </KpiCard>
      </div>

      {/* ---- Bottom row: latest in workshop + attention ---- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-red-500" /> Latest Assets in Workshop
              </CardTitle>
              <Badge variant="secondary">Total {workshopRows.length}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">Showing latest 5 assets with active job cards.</p>
          </CardHeader>
          <CardContent>
            {workshopRows.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">No assets in workshop</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground border-b">
                  <tr>
                    <th className="text-left py-1">Asset</th>
                    <th className="text-left py-1">Job Cards</th>
                    <th className="text-left py-1">Type</th>
                  </tr>
                </thead>
                <tbody>
                  {workshopRows.map(({ asset, jobCards }) => (
                    <tr key={asset.id} className="border-b last:border-0">
                      <td className="py-1.5">
                        <Link to="/fleet/assets" className="hover:underline">
                          {asset.fleetNumber || asset.registrationNumber || asset.id}
                        </Link>
                      </td>
                      <td className="py-1.5">{jobCards}</td>
                      <td className="py-1.5 text-muted-foreground">{asset.assetType || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-500" /> Attention Required
              </CardTitle>
              <Badge variant="secondary">Items {attentionItems.length}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {attentionItems.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center">All clear</div>
            ) : attentionItems.map((a, i) => (
              <div key={i} className="px-2 py-2 border rounded-md">
                <div className="text-sm font-medium mb-1">{a.assetLabel}</div>
                <div className="space-y-0.5">
                  {a.reasons.map((r, j) => (
                    <Link
                      key={j}
                      to={reasonHref(a.assetLabel, r.category)}
                      className="flex items-center justify-between gap-2 text-xs text-red-600 dark:text-red-400 hover:underline rounded px-1 py-0.5"
                    >
                      <span>{r.text}</span>
                      <ChevronRight className="h-3 w-3 opacity-60 flex-shrink-0" />
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="kanban" className="mt-0">
          <JobCardKanban />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---------- Kanban ----------

function useIsMobileViewport(): boolean {
  const [isMobile, setIsMobile] = React.useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = () => setIsMobile(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);
  return isMobile;
}

const KANBAN_COLUMNS: Array<{ key: JobCardStatus; label: string; accent: string }> = [
  { key: 'active',    label: 'Active',    accent: 'bg-blue-500' },
  { key: 'completed', label: 'Completed', accent: 'bg-amber-500' },
  { key: 'approved',  label: 'Approved',  accent: 'bg-green-500' },
  { key: 'voided',    label: 'Voided',    accent: 'bg-red-500' },
];

function JobCardKanban() {
  const qc = useQueryClient();
  const isMobile = useIsMobileViewport();
  const queryKey = ['kanban', '/fleet/job-cards', 'all'] as const;
  const { data: jobs = [], isLoading } = useQuery<JobCardKanban[]>({
    queryKey,
    queryFn: async () => (await api<JobCardKanban[]>('/fleet/job-cards?limit=1000')).data || [],
  });

  const [dragging, setDragging] = React.useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = React.useState<JobCardStatus | null>(null);
  // Mobile: every column is collapsible and starts collapsed. Desktop ignores this set.
  const [collapsedCols, setCollapsedCols] = React.useState<Set<JobCardStatus>>(
    () => new Set<JobCardStatus>(['active', 'completed', 'approved', 'voided']),
  );
  const toggleCol = (key: JobCardStatus) => {
    setCollapsedCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: JobCardStatus }) => {
      const r = await api(`/fleet/job-cards/${id}`, { method: 'PUT', body: JSON.stringify({ status }) });
      if (!r.success) throw new Error(r.error?.message || 'Failed to update');
    },
    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey });
      const prev = qc.getQueryData<JobCardKanban[]>(queryKey);
      qc.setQueryData<JobCardKanban[]>(queryKey, (cur) =>
        (cur ?? []).map((j) => (j.id === id ? { ...j, status } : j))
      );
      return { prev };
    },
    onError: (e: any, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(queryKey, ctx.prev);
      toast('Update failed', e.message, 'error');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey });
      // Also refresh the stats aggregate so the Overview tab counters match.
      qc.invalidateQueries({ queryKey: ['dashboard', '/fleet/job-cards/stats'] });
    },
  });

  const byStatus = React.useMemo(() => {
    const m: Record<JobCardStatus, JobCardKanban[]> = { active: [], completed: [], approved: [], voided: [] };
    for (const j of jobs) {
      const k = (j.status as JobCardStatus) ?? 'active';
      (m[k] ?? m.active).push(j);
    }
    return m;
  }, [jobs]);

  function handleDrop(target: JobCardStatus) {
    const id = dragging;
    setDragging(null);
    setDragOverCol(null);
    if (!id) return;
    const current = jobs.find((j) => j.id === id);
    if (!current || current.status === target) return;
    updateStatus.mutate({ id, status: target });
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
      {KANBAN_COLUMNS.map((col) => {
        const items = byStatus[col.key] ?? [];
        const isOver = dragOverCol === col.key;
        const collapsed = isMobile && collapsedCols.has(col.key);
        return (
          <div
            key={col.key}
            className={cn(
              'rounded-lg border bg-muted/30 flex flex-col',
              !collapsed && 'min-h-[300px]',
              isOver && 'ring-2 ring-primary/60 bg-primary/5',
            )}
            onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.key); }}
            onDragLeave={() => setDragOverCol((cur) => (cur === col.key ? null : cur))}
            onDrop={() => handleDrop(col.key)}
          >
            <div
              className={cn(
                'flex items-center justify-between px-3 py-2',
                !collapsed && 'border-b',
                isMobile && 'cursor-pointer select-none',
              )}
              onClick={isMobile ? () => toggleCol(col.key) : undefined}
              role={isMobile ? 'button' : undefined}
              aria-expanded={isMobile ? !collapsed : undefined}
              tabIndex={isMobile ? 0 : undefined}
              onKeyDown={isMobile ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCol(col.key); }
              } : undefined}
            >
              <div className="flex items-center gap-2">
                {isMobile && (
                  collapsed
                    ? <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                )}
                <span className={cn('w-2 h-2 rounded-full', col.accent)} />
                <span className="text-sm font-medium">{col.label}</span>
              </div>
              <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
            </div>
            {!collapsed && (
              <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[70vh]">
                {isLoading && items.length === 0 && (
                  <div className="text-xs text-muted-foreground py-4 text-center">Loading…</div>
                )}
                {!isLoading && items.length === 0 && (
                  <div className="text-xs text-muted-foreground py-4 text-center">No job cards</div>
                )}
                {items.map((j) => (
                  <KanbanCard
                    key={j.id}
                    job={j}
                    dragging={dragging === j.id}
                    onDragStart={() => setDragging(j.id)}
                    onDragEnd={() => { setDragging(null); setDragOverCol(null); }}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function KanbanCard({
  job, dragging, onDragStart, onDragEnd,
}: {
  job: JobCardKanban;
  dragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
}) {
  const assignees = (job.assignedTo ?? []).map((a) => a.fullName).filter(Boolean) as string[];
  return (
    <Link
      to={`/fleet/job-cards/${job.id}/service`}
      state={{ from: '/job-cards/dashboard?tab=kanban' }}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={cn(
        'block rounded-md border bg-card p-2.5 text-xs space-y-1.5 cursor-grab hover:bg-accent/40 transition-colors',
        dragging && 'opacity-50',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium truncate">{job.jobSheetNumber || job.id}</span>
        {job.asset?.fleetNumber && (
          <Badge variant="outline" className="text-[10px] shrink-0">{job.asset.fleetNumber}</Badge>
        )}
      </div>
      {job.workDescription && (
        <div className="text-muted-foreground line-clamp-2">{job.workDescription}</div>
      )}
      {assignees.length > 0 && (
        <div className="flex items-center gap-1 text-muted-foreground">
          <User className="h-3 w-3" />
          <span className="truncate">{assignees.join(', ')}</span>
        </div>
      )}
    </Link>
  );
}

// ---------- helpers ----------

function KpiCard({
  title, icon: Icon, totalLabel, children,
}: {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  totalLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-1">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Icon className="h-4 w-4 text-red-500" /> {title}
          </CardTitle>
          <Badge variant="secondary" className="text-[10px]">{totalLabel}</Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  );
}

function Bar({ value, max, label, colour = 'bg-red-500' }: { value: number; max: number; label: string; colour?: string }) {
  const pct = max ? (value / max) * 100 : 0;
  return (
    <div className="flex flex-col items-center gap-1 flex-1">
      <div className="text-[10px] text-muted-foreground">{value}</div>
      <div className="h-20 w-8 bg-muted rounded-sm flex items-end overflow-hidden">
        <div className={`${colour} w-full transition-all`} style={{ height: `${pct}%` }} />
      </div>
      <div className="text-[10px] text-muted-foreground text-center">{label}</div>
    </div>
  );
}

function Legend({ overdue, upcoming }: { overdue: number; upcoming: number }) {
  return (
    <div className="flex items-center justify-center gap-4 text-[10px] text-muted-foreground pt-1">
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> Overdue {overdue}</span>
      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" /> Upcoming {upcoming}</span>
    </div>
  );
}

function ComplianceRow({ label, overdue, upcoming, max }: { label: string; overdue: number; upcoming: number; max: number }) {
  const o = max ? (overdue / max) * 100 : 0;
  const u = max ? (upcoming / max) * 100 : 0;
  return (
    <div className="grid grid-cols-[40px_1fr_24px] gap-2 items-center">
      <span className="text-muted-foreground">{label}</span>
      <div className="space-y-0.5">
        <div className="h-1.5 bg-muted rounded-sm overflow-hidden">
          <div className="h-full bg-red-500" style={{ width: `${o}%` }} />
        </div>
        <div className="h-1.5 bg-muted rounded-sm overflow-hidden">
          <div className="h-full bg-green-500" style={{ width: `${u}%` }} />
        </div>
      </div>
      <div className="text-right text-[10px]">
        <div>{overdue}</div>
        <div className="text-muted-foreground">{upcoming}</div>
      </div>
    </div>
  );
}
